const resources = {
  "en": {
    "setup": {
      "title": "Setup Your Profile",
      "subtitle": "Personalize your workspace before diving into insights.",
      "heroTag": "Getting started",
      "returningHeroTag": "Welcome back",
      "returningTitle": "Review your profile settings",
      "returningSubtitle": "Keep your workspace current by updating language, currency, and alerts.",
      "returningStatusLabel": "Profile saved",
      "returningStatusHint": "Profile saved—update settings anytime.",
      "progressLabel": "Setup progress",
      "themeLight": "Light",
      "themeDark": "Dark",
      "baseCurrency": {
        "revaluationNotice": "Changing your base currency will recalculate historical balances in the selected currency.",
        "revaluationNoticeConfirm": "Changing your base currency will recalculate historical balances in that currency. This may take a moment.",
        "lastRevaluation": "Last revaluation: {{date}}",
        "notRevaluedYet": "Not revalued yet",
        "changeCta": "Change base currency",
        "confirmTitle": "Change base currency?",
        "confirmAction": "Revalue portfolio",
        "confirmCancel": "Cancel"
      },
      "sections": {
        "profile": {
          "title": "Profile basics",
          "description": "Set your default country, language, currency, and timezone to tailor insights."
        },
        "preferences": {
          "title": "Workspace preferences",
          "description": "Choose how FinApp should calculate balances and time equivalents."
        },
        "notifications": {
          "title": "Stay in the loop",
          "description": "Decide which alerts you want to receive as things change."
        }
      },
      "steps": {
        "profile": "Profile basics",
        "profileDescription": "Tell us where you operate and how you work.",
        "accounts": "Connect accounts",
        "accountsDescription": "Sync your banks and wallets for live balances.",
        "finish": "Finishing touches",
        "finishDescription": "Fine-tune alerts and start exploring."
      },
      "country": "Country",
      "selectCountry": "Select country",
      "language": "Language",
      "selectLanguage": "Select language",
      "currency": "Currency",
      "selectCurrency": "Select currency",
      "timezone": "Timezone",
      "selectTimezone": "Select timezone",
      "liveBalanceAccounts": "Live Balance Accounts",
      "selectAccounts": "Select accounts",
      "liveBalanceDescription": "Pick the live accounts you want to include in dashboards by default.",
      "showAmounts": "Show amounts",
      "showAmountsDescription": "Enabled — amounts are visible. Disabled — amounts are hidden for privacy.",
      "showTimeEquivalent": "Show hour equivalent near totals",
      "timeEquivalentDescription": "Display totals as billable hours alongside your currency.",
      "hourlyRate": "Hourly rate",
      "hourlyRatePlaceholder": "Enter your hourly rate",
      "hourlyRateRequired": "Enter your hourly rate to show hours",
      "hourlyRatePositive": "Hourly rate must be greater than 0",
      "hourlyRateHelper": "We use this to translate balances into an hourly view for planning.",
      "notifyEmail": "Email notifications",
      "notifyEmailDescription": "Get summaries and important updates in your inbox.",
      "notifySms": "SMS notifications",
      "notifySmsDescription": "Receive quick texts for time-sensitive alerts.",
      "notifyPush": "Push notifications",
      "notifyPushDescription": "Enable real-time push notifications on your devices.",
      "submitHint": "You can revisit these preferences anytime from the profile menu.",
      "theme": "Theme",
      "save": "Save",
      "saveAndContinue": "Save and continue",
      "messages": {
        "loadFailed": "Failed to load profile settings. {{error}}",
        "profileUpdated": "Profile updated.",
        "profileUpdatedRevalueQueued": "Profile updated. Portfolio revaluation queued.",
        "updateFailed": "Unable to update profile. {{error}}",
        "pushPermissionDenied": "Push notifications are enabled in settings, but device permission is denied.",
        "pushPermissionDismissed": "Push notifications are enabled in settings, but device permission is still pending.",
        "pushUnsupported": "Push notifications are not supported in this runtime.",
        "pushSyncFailed": "Profile saved, but push sync failed. {{error}}",
        "baseCurrencyUpdatedRevalueQueued": "Base currency updated. Portfolio revaluation queued.",
        "baseCurrencyUpdateFailed": "Unable to update base currency. {{error}}",
        "revalueFailed": "Unable to revalue portfolio. {{error}}"
      }
    }
  },
  "uk": {
    "setup": {
      "title": "Налаштуйте свій профіль",
      "subtitle": "Персоналізуйте робочий простір перед тим, як перейти до інсайтів.",
      "heroTag": "Початок роботи",
      "returningHeroTag": "З поверненням",
      "returningTitle": "Перевірте налаштування профілю",
      "returningSubtitle": "Оновіть мову, валюту та сповіщення, щоб робочий простір був актуальним.",
      "returningStatusLabel": "Профіль збережено",
      "returningStatusHint": "Профіль збережено — оновлюйте налаштування будь-коли.",
      "progressLabel": "Прогрес налаштування",
      "themeLight": "Світла",
      "themeDark": "Темна",
      "baseCurrency": {
        "revaluationNotice": "Зміна базової валюти перераховує історичні баланси у вибраній валюті.",
        "revaluationNoticeConfirm": "Зміна базової валюти перераховує історичні баланси в цій валюті. Це може зайняти деякий час.",
        "lastRevaluation": "Остання переоцінка: {{date}}",
        "notRevaluedYet": "Ще не переоцінено",
        "changeCta": "Змінити базову валюту",
        "confirmTitle": "Змінити базову валюту?",
        "confirmAction": "Запустити переоцінку",
        "confirmCancel": "Скасувати"
      },
      "sections": {
        "profile": {
          "title": "Основи профілю",
          "description": "Вкажіть країну, мову, валюту та часовий пояс за замовчуванням, щоб налаштувати інсайти."
        },
        "preferences": {
          "title": "Налаштування робочого простору",
          "description": "Виберіть, як FinApp має рахувати баланси та часові еквіваленти."
        },
        "notifications": {
          "title": "Залишайтеся в курсі",
          "description": "Оберіть сповіщення, які хочете отримувати при змінах."
        }
      },
      "steps": {
        "profile": "Основи профілю",
        "profileDescription": "Розкажіть, де ви працюєте і як працюєте.",
        "accounts": "Підключіть рахунки",
        "accountsDescription": "Синхронізуйте банки й гаманці для актуальних балансів.",
        "finish": "Останні штрихи",
        "finishDescription": "Налаштуйте сповіщення й почніть досліджувати."
      },
      "country": "Країна",
      "selectCountry": "Виберіть країну",
      "language": "Мова",
      "selectLanguage": "Виберіть мову",
      "currency": "Валюта",
      "selectCurrency": "Виберіть валюту",
      "timezone": "Часовий пояс",
      "selectTimezone": "Виберіть часовий пояс",
      "liveBalanceAccounts": "Рахунки з живими балансами",
      "selectAccounts": "Виберіть рахунки",
      "liveBalanceDescription": "Оберіть рахунки з актуальними балансами для панелей за замовчуванням.",
      "showAmounts": "Показувати суми",
      "showAmountsDescription": "Увімкнено — суми видно. Вимкнено — суми приховані для приватності.",
      "showTimeEquivalent": "Показувати еквівалент у годинах біля підсумків",
      "timeEquivalentDescription": "Показує підсумки як оплачувані години поруч із валютою.",
      "hourlyRate": "Погодинна ставка",
      "hourlyRatePlaceholder": "Введіть погодинну ставку",
      "hourlyRateRequired": "Введіть погодинну ставку, щоб показувати години",
      "hourlyRatePositive": "Погодинна ставка має бути більшою за 0",
      "hourlyRateHelper": "Ми використовуємо це, щоб перевести баланси в погодинний вигляд для планування.",
      "notifyEmail": "Email-сповіщення",
      "notifyEmailDescription": "Отримуйте зведення та важливі оновлення на пошту.",
      "notifySms": "SMS-сповіщення",
      "notifySmsDescription": "Отримуйте швидкі повідомлення про термінові зміни.",
      "notifyPush": "Push-сповіщення",
      "notifyPushDescription": "Увімкніть push-сповіщення в реальному часі на пристроях.",
      "submitHint": "Ви можете повернутися до цих налаштувань у меню профілю будь-коли.",
      "theme": "Тема",
      "save": "Зберегти",
      "saveAndContinue": "Зберегти та продовжити",
      "messages": {
        "loadFailed": "Не вдалося завантажити налаштування профілю. {{error}}",
        "profileUpdated": "Профіль оновлено.",
        "profileUpdatedRevalueQueued": "Профіль оновлено. Переоцінку портфеля заплановано.",
        "updateFailed": "Не вдалося оновити профіль. {{error}}",
        "pushPermissionDenied": "Push-сповіщення увімкнені в налаштуваннях, але дозвіл на пристрої заборонений.",
        "pushPermissionDismissed": "Push-сповіщення увімкнені в налаштуваннях, але дозвіл на пристрої ще не надано.",
        "pushUnsupported": "Push-сповіщення не підтримуються в цьому runtime.",
        "pushSyncFailed": "Профіль збережено, але синхронізація push не вдалась. {{error}}",
        "baseCurrencyUpdatedRevalueQueued": "Базову валюту оновлено. Переоцінку портфеля заплановано.",
        "baseCurrencyUpdateFailed": "Не вдалося оновити базову валюту. {{error}}",
        "revalueFailed": "Не вдалося запустити переоцінку. {{error}}"
      }
    }
  }
};

export default resources;
